<template>
  <div
    ref="stickyElement"
    :style="style"
    :class="{
      'sticky--is-absolute': isSticky,
      'sticky--is-absolute-100': isSticky100,
      'sticky--is-absolute-200': isSticky200,
    }"
  >
    <slot />
  </div>
</template>

<script setup>
/**
 * When stickyElement reaches bottomAffixElement + offset it will be
 * fixed there via absolute and stay at that position
 *
 * Offset can be set in CSS via `--sticky-offset` and is updated
 * automatically when breakpoints change
 */

import {
  useElementBounding,
  useMounted,
  useWindowScroll,
  useWindowSize,
} from '@vueuse/core';
import { computed, ref, toRefs, watch } from 'vue-demi';
import { useStore } from 'vuex';

const props = defineProps({
  bottomAffix: {
    type: String,
  },
});

const { bottomAffix } = toRefs(props);
const bottomAffixElement = document.querySelector(bottomAffix.value);

const { height: windowHeight } = useWindowSize();
const { y: scrollTopY } = useWindowScroll();
const scrollBottomY = computed(() => scrollTopY.value + windowHeight.value);

const stickyElement = ref(undefined);

const store = useStore();
const breakpoint = computed(() => store.getters['window/breakpoint']);
const stickyOffset = ref(0);
watch(
  [breakpoint, stickyElement],
  ([breakpoint, stickyElement]) => {
    if (!stickyElement) return;
    stickyOffset.value = Number(
      window.getComputedStyle(stickyElement).getPropertyValue('--sticky-offset')
    );
  },
  { immediate: true }
);

const bodyElement = document.querySelector('body');
const { height: bodyHeight } = useElementBounding(bodyElement);

const absoluteBottom = computed(
  () => bodyHeight.value - (bottomAffixElement.offsetTop - stickyOffset.value)
);

const turnStickyAt = computed(() => bodyHeight.value - absoluteBottom.value);
const toStickyDistance = computed(
  () => turnStickyAt.value - scrollBottomY.value
);

const isMounted = useMounted();
const isSticky = computed(() => {
  if (!isMounted.value) return false;
  return toStickyDistance.value <= 0;
});

const isSticky100 = computed(() => {
  if (!isMounted.value) return false;
  return toStickyDistance.value <= -100;
});

const isSticky200 = computed(() => {
  if (!isMounted.value) return false;
  return toStickyDistance.value <= -200;
});

const style = computed(() => {
  if (!isSticky.value) return {};

  return {
    position: 'absolute',
    bottom: `${absoluteBottom.value}px`,
  };
});
</script>
